import { Tag, TagType } from '@/types/tag';

/**
 * Utility for mapping between Supabase Database rows and Frontend Tag objects.
 * Centralizing this ensures that schema changes only need to be updated in one place.
 */

// SEC-M7 phase 17: explicit column allowlist for `tags` reads (base columns only,
// no joined `state` / `activeStates` — those are pulled via separate query shapes
// when needed). Keep in sync with `dbRowToTag` and the local mappers in
// `useStockCategories.ts` and `useLocations.ts` (`mapRowToCategory` / `dbRowToLocation`).
// Replacing `.select('*')` with `.select(TAG_COLUMNS)` prevents future internal
// columns added to `tags` (e.g. moderation flags, internal scoring, billing-tier
// gates, audit fields) from auto-flowing to every authenticated client — `tags`
// is one of the highest-fanout config tables (categories, locations, stores all
// live here) so the blast radius matters.
export const TAG_COLUMNS =
  'id, team_id, parent_id, tag_type, name, emoji, color, metadata, ' +
  'sort_order, created_at, current_state_id';

export const dbRowToTag = (row: any): Tag => {
  const metadataColor = row?.metadata?.color;
  return {
    id: row.id,
    teamId: row.team_id,
    parentId: row.parent_id,
    tagType: row.tag_type as TagType,
    name: row.name,
    emoji: row.emoji,
    color: row.color || metadataColor,
    metadata: row.metadata || {},
    sortOrder: row.sort_order ?? 0,
    createdAt: new Date(row.created_at),
    currentStateId: row.current_state_id,
    state: row.state ? {
      id: row.state.id,
      teamId: row.state.team_id,
      tagId: row.state.tag_id || null,
      stateGroupId: row.state.state_group_id || null,
      name: row.state.name,
      emoji: row.state.emoji,
      color: row.state.color,
      isOptimal: row.state.is_optimal,
      sortOrder: row.state.sort_order ?? null,
      createdAt: new Date(row.state.created_at),
    } : null,
    activeStates: (row.activeStates || row.active_states || []).map((as: any) => ({
      stateId: as.state_id || as.stateId,
      stateGroupId: as.state_group_id || as.stateGroupId,
      state: as.state ? {
        id: as.state.id,
        teamId: as.state.team_id || as.state.teamId,
        tagId: as.state.tag_id || as.state.tagId || null,
        stateGroupId: as.state.state_group_id || as.state.stateGroupId || null,
        name: as.state.name,
        emoji: as.state.emoji,
        color: as.state.color,
        isOptimal: as.state.is_optimal || as.state.isOptimal,
        sortOrder: as.state.sort_order ?? as.state.sortOrder ?? null,
        createdAt: new Date(as.state.created_at || as.state.createdAt),
      } : undefined
    })),
  };
};

export const mapTagToDb = (updates: Partial<Tag>): any => {
  const dbUpdates: any = {};

  if ('name' in updates && updates.name !== undefined) dbUpdates.name = updates.name.trim();
  if ('teamId' in updates) dbUpdates.team_id = updates.teamId;
  if ('parentId' in updates) dbUpdates.parent_id = updates.parentId;
  if ('tagType' in updates) dbUpdates.tag_type = updates.tagType;
  if ('emoji' in updates) dbUpdates.emoji = updates.emoji;
  if ('color' in updates) dbUpdates.color = updates.color;
  if ('metadata' in updates) dbUpdates.metadata = updates.metadata;
  if ('sortOrder' in updates) dbUpdates.sort_order = updates.sortOrder;
  if ('currentStateId' in updates) dbUpdates.current_state_id = updates.currentStateId;

  return dbUpdates;
};

/**
 * Specifically for cache serialization/deserialization where keys might already be camelCase
 */
export const deserializeTagFromCache = (row: any): Tag => {
  const createdAt = row.createdAt ?? row.created_at;
  return {
    id: row.id,
    teamId: row.teamId ?? row.team_id,
    parentId: row.parentId ?? row.parent_id ?? null,
    tagType: row.tagType ?? row.tag_type,
    name: row.name,
    emoji: row.emoji,
    color: row.color ?? row?.metadata?.color,
    metadata: row.metadata || {},
    sortOrder: row.sortOrder ?? row.sort_order ?? 0,
    createdAt: createdAt ? new Date(createdAt) : new Date(),
    currentStateId: row.currentStateId ?? row.current_state_id,
    state: row.state ? {
      ...row.state,
      createdAt: new Date(row.state.createdAt),
    } : null,
    activeStates: row.activeStates || [],
  };
};
